import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { ParseKeys } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ApproveApplicationDialog } from '@/admin/applications/ApproveApplicationDialog';
import { applicationRefusalKey } from '@/admin/applications/applicationErrors';
import { RejectApplicationDialog } from '@/admin/applications/RejectApplicationDialog';
import buttonStyles from '@/admin/styles/button.module.css';
import type { BadgeVariant } from '@/admin/ui/Badge';
import { Badge } from '@/admin/ui/Badge';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import { InfoNotice } from '@/admin/ui/Form';
import { PageTitle } from '@/admin/ui/Page';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';

type ApplicationStatus = components['schemas']['ApplicationStatus'];
type HelperApplicationRole = components['schemas']['HelperApplicationRole'];

const statusVariant: Record<ApplicationStatus, BadgeVariant> = {
  approved: 'success',
  rejected: 'error',
  pending: 'warning',
  discarded: 'neutral',
};

// Which of the three intake forms this row came from, and therefore which
// polymorphic identity Approve mints -- `service.py:_create_follow_up_user`
// picks the class off `application.role`. Spellings match the queue's own map
// (`applications.index.tsx`): the same `admin.applications.role.*` keys.
const roleLabels = {
  STUDY_HELPER: 'admin.applications.role.studyHelper',
  FOLLOWUP_COORDINATOR: 'admin.applications.role.followupCoordinator',
  FOLLOWUP_PARTNER: 'admin.applications.role.followupPartner',
} as const satisfies Record<HelperApplicationRole, ParseKeys>;

export const Route = createFileRoute('/admin/applications/$applicationId')({
  staticData: { minTier: 'FRONT_END_SUPPORT' },
  component: ApplicationDetail,
});

const formatLabel = (key: string): string => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

function ApplicationDetail() {
  const { t } = useTranslation();
  const { applicationId } = Route.useParams();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectMessage, setRejectMessage] = useState('');
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const {
    data: application,
    isLoading,
    error,
  } = api.getHelperApplication.useQuery({
    path: { application_id: Number(applicationId) },
  });

  const approveMutation = api.approveHelperApplication.useMutation(undefined, {
    meta: { toast: t('admin.applications.approved') },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: api.getHelperApplication.getQueryKey({
          path: { application_id: Number(applicationId) },
        }),
      });
      queryClient.invalidateQueries({
        queryKey: api.listHelperApplications.getQueryKey(),
      });
    },
  });

  const rejectMutation = api.rejectHelperApplication.useMutation(undefined, {
    meta: { toast: t('admin.applications.rejected') },
    onSuccess: () => {
      setRejectDialogOpen(false);
      setRejectMessage('');
      queryClient.invalidateQueries({
        queryKey: api.getHelperApplication.getQueryKey({
          path: { application_id: Number(applicationId) },
        }),
      });
      queryClient.invalidateQueries({
        queryKey: api.listHelperApplications.getQueryKey(),
      });
    },
  });

  const discardMutation = api.discardHelperApplication.useMutation(undefined, {
    meta: { toast: t('admin.applications.discarded') },
    onSuccess: () => {
      setDiscardDialogOpen(false);
      queryClient.invalidateQueries({
        queryKey: api.getHelperApplication.getQueryKey({
          path: { application_id: Number(applicationId) },
        }),
      });
      queryClient.invalidateQueries({
        queryKey: api.listHelperApplications.getQueryKey(),
      });
    },
  });

  const handleApprove = () => {
    // Follow-up roles are never placed. The generated type requires both fields
    // although the server defaults them, so the no-placement body is spelled out.
    approveMutation.mutate({
      path: { application_id: Number(applicationId) },
      body: { placement_mode: 'none', join_community_group: false },
    });
  };

  const handleReject = () => {
    rejectMutation.mutate({
      path: { application_id: Number(applicationId) },
      body: { rejection_reason: rejectMessage },
    });
  };

  if (isLoading) {
    return (
      <LoadingMessage>{t('admin.applications.loadingOne')}</LoadingMessage>
    );
  }

  if (error) {
    return (
      <ErrorMessage>{t('admin.applications.errorLoadingOne')}</ErrorMessage>
    );
  }

  if (!application) {
    return <ErrorMessage>{t('admin.applications.notFound')}</ErrorMessage>;
  }

  const isPending = application.status === 'pending';
  const isFollowUp = application.role !== 'STUDY_HELPER';
  // The server refuses to approve these (409 existing_account_application):
  // legacy aliased the applicant onto the matched account, which has no
  // service here yet.
  const existingAccount =
    application.has_existing_account === true &&
    Boolean(application.existing_account_email);
  // The study-helper dialog reports its own refusals; this is the follow-up
  // roles' direct Approve and the Discard confirm, which have no dialog body.
  const actionError = isFollowUp ? approveMutation.error : null;
  const discardError = discardMutation.error;
  const busy =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    discardMutation.isPending;
  // Key-agnostic on purpose: the follow-up forms answer four screening
  // questions (GF 12 fields 74/75/76/83, GF 14 fields 68/69/70/87) whose keys
  // are the request's own field names, and GF-4 study-helper rows carry `{}`.
  const screeningResponses = Object.entries(application.screening_responses);

  return (
    <Container>
      <BackLink to="/admin/applications/">
        {t('admin.applications.backToList')}
      </BackLink>

      <Header>
        <TitleGroup>
          <PageTitle>
            {application.first_name} {application.last_name}
          </PageTitle>
          <Badge $variant="neutral" $size="md">
            {t(roleLabels[application.role])}
          </Badge>
        </TitleGroup>
        {isPending ? (
          <ActionButtons>
            {!existingAccount && (
              <Button
                className={buttonStyles.Button}
                // Only study helpers are placed in a congregation; the
                // follow-up roles approve directly with an empty body.
                onClick={
                  isFollowUp ? handleApprove : () => setApproveDialogOpen(true)
                }
                disabled={busy}
              >
                {approveMutation.isPending
                  ? t('admin.applications.approving')
                  : t('admin.applications.approve')}
              </Button>
            )}
            <RejectApplicationDialog
              open={rejectDialogOpen}
              onOpenChange={setRejectDialogOpen}
              rejectMessage={rejectMessage}
              onRejectMessageChange={setRejectMessage}
              onReject={handleReject}
              isPending={rejectMutation.isPending}
              disabled={busy}
            />
            <Button
              className={buttonStyles.Button}
              onClick={() => setDiscardDialogOpen(true)}
              disabled={busy}
            >
              {t('admin.applications.discard')}
            </Button>
          </ActionButtons>
        ) : (
          <Badge $variant={statusVariant[application.status]} $size="md">
            {application.status}
          </Badge>
        )}
      </Header>

      {actionError && (
        <ActionError role="alert">
          {t(
            applicationRefusalKey(actionError) ??
              'admin.applications.placement.failed',
          )}
        </ActionError>
      )}

      {isPending && existingAccount && (
        <InfoNotice>
          {t('admin.applications.existingAccountNotice', {
            email: application.existing_account_email,
            interpolation: { escapeValue: false },
          })}
        </InfoNotice>
      )}

      {!isPending && (
        <Section>
          <SectionTitle>{t('admin.applications.review')}</SectionTitle>
          <FieldGrid>
            <Field>
              <FieldLabel>{t('admin.applications.reviewedAt')}</FieldLabel>
              <FieldValue>
                {application.reviewed_at
                  ? new Date(application.reviewed_at).toLocaleString()
                  : '—'}
              </FieldValue>
            </Field>
            <Field>
              <FieldLabel>{t('admin.applications.reviewedBy')}</FieldLabel>
              <FieldValue>{application.reviewed_by_name || '—'}</FieldValue>
            </Field>
            {application.status === 'rejected' && (
              <Field>
                <FieldLabel>
                  {t('admin.applications.rejectionReason')}
                </FieldLabel>
                <FieldValue>{application.rejection_reason || '—'}</FieldValue>
              </Field>
            )}
            {application.created_account_id !== null && (
              <Field>
                <FieldLabel>{t('admin.applications.account')}</FieldLabel>
                <FieldValue>
                  #{application.created_account_id}{' '}
                  <Link
                    to="/admin/people/$userId/"
                    params={{ userId: String(application.created_account_id) }}
                  >
                    {t('admin.applications.openInPeople')}
                  </Link>
                </FieldValue>
              </Field>
            )}
          </FieldGrid>
        </Section>
      )}

      <Section>
        <SectionTitle>
          {t('admin.applications.personalInformation')}
        </SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>{t('admin.common.email')}</FieldLabel>
            <FieldValue>{application.email}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.common.phone')}</FieldLabel>
            <FieldValue>{application.phone || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.applications.whatsapp')}</FieldLabel>
            <FieldValue>{application.whatsapp || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.students.gender')}</FieldLabel>
            <FieldValue>{application.gender || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.applications.birthdate')}</FieldLabel>
            <FieldValue>
              {application.birthdate
                ? new Date(application.birthdate).toLocaleDateString()
                : '—'}
            </FieldValue>
          </Field>
          {/* Both age signals, side by side, because exactly one of them is
              ever populated: GF 12/14 ask for a birth YEAR, so `birthdate` is
              NULL by construction on every follow-up row (DIV-4,
              `helper_applications/models.py`). Rendering only Birthdate showed
              the approver an em-dash where the answer exists. */}
          <Field>
            <FieldLabel>{t('admin.applications.birthYear')}</FieldLabel>
            <FieldValue>{application.birth_year ?? '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>
              {t('admin.applications.preferredLanguages')}
            </FieldLabel>
            <FieldValue>
              {application.preferred_languages?.join(', ') || '—'}
            </FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>{t('admin.applications.address')}</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>{t('admin.applications.addressLine1')}</FieldLabel>
            <FieldValue>{application.address_1 || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.applications.addressLine2')}</FieldLabel>
            <FieldValue>{application.address_2 || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.common.city')}</FieldLabel>
            <FieldValue>{application.city || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.studyHelpers.state')}</FieldLabel>
            <FieldValue>{application.state || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.applications.zip')}</FieldLabel>
            <FieldValue>{application.zip || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.common.country')}</FieldLabel>
            <FieldValue>{application.country || '—'}</FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>
          {t('admin.applications.congregationInformation')}
        </SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>{t('admin.applications.congregationName')}</FieldLabel>
            <FieldValue>{application.congregation_name || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.applications.website')}</FieldLabel>
            <FieldValue>{application.congregation_website || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.applications.addressLine1')}</FieldLabel>
            <FieldValue>{application.congregation_address_1 || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.applications.addressLine2')}</FieldLabel>
            <FieldValue>{application.congregation_address_2 || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.common.city')}</FieldLabel>
            <FieldValue>{application.congregation_city || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.studyHelpers.state')}</FieldLabel>
            <FieldValue>{application.congregation_state || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.applications.zip')}</FieldLabel>
            <FieldValue>{application.congregation_zip || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.common.country')}</FieldLabel>
            <FieldValue>{application.congregation_country || '—'}</FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      {isFollowUp && (
        <Section>
          <SectionTitle>
            {t('admin.applications.followUpAssignment')}
          </SectionTitle>
          <FieldGrid>
            {/* THE APPROVAL-CRITICAL FIELD. Approve copies this list onto the
                created account's `approved_countries`
                (`service.py:_create_follow_up_user`), which is the column
                W4.7's network directory and W4.8's Nations cell read -- so with
                it unrendered the administrator approved the coordinator's
                serving countries blind. Raw codes, like the Country fields
                above and below: this page has no country-name lookup and
                adding one for a single field would leave seven coded and one
                named. An em-dash is real signal, not a gap -- 8 of 14 migrated
                GF-14 entries carry no field 67 at all
                (`helper_applications/models.py`), and approving one of those
                mints an account serving no country. */}
            <Field>
              <FieldLabel>{t('admin.applications.countriesServed')}</FieldLabel>
              <FieldValue>
                {application.follow_up_countries?.length
                  ? application.follow_up_countries.join(', ')
                  : '—'}
              </FieldValue>
            </Field>
            {/* GF 12 `82.1` / GF 14 `86.1`. No destination on `users`, so this
                row is the only store (§10.8) and this page is the only reader
                anyone gets. */}
            <Field>
              <FieldLabel>
                {t('admin.applications.contactSharingConsent')}
              </FieldLabel>
              <FieldValue>
                {application.share_contact_consent === null
                  ? '—'
                  : application.share_contact_consent
                    ? t('admin.common.yes')
                    : t('admin.common.no')}
              </FieldValue>
            </Field>
          </FieldGrid>
        </Section>
      )}

      <Section>
        <SectionTitle>{t('admin.applications.radioResponses')}</SectionTitle>
        {Object.entries(application.radio_responses).map(([key, value]) => (
          <AgreementItem key={key}>
            <AgreementLabel>{formatLabel(key)}</AgreementLabel>
            <Badge $variant={value === 'agree' ? 'success' : 'neutral'}>
              {formatLabel(value)}
            </Badge>
          </AgreementItem>
        ))}
      </Section>

      {screeningResponses.length > 0 && (
        <Section>
          <SectionTitle>{t('admin.applications.screening')}</SectionTitle>
          <ScreeningList>
            {screeningResponses.map(([key, value]) => (
              <Field key={key}>
                <FieldLabel>{formatLabel(key)}</FieldLabel>
                <ScreeningAnswer>{value}</ScreeningAnswer>
              </Field>
            ))}
          </ScreeningList>
        </Section>
      )}

      <Section>
        <SectionTitle>
          {t('admin.applications.additionalInformation')}
        </SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>{t('admin.applications.referralSource')}</FieldLabel>
            <FieldValue>{application.referral_source || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>
              {t('admin.applications.hasExistingAccount')}
            </FieldLabel>
            <FieldValue>
              {application.has_existing_account === null
                ? '—'
                : application.has_existing_account
                  ? t('admin.common.yes')
                  : t('admin.common.no')}
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>
              {t('admin.applications.existingAccountEmail')}
            </FieldLabel>
            <FieldValue>{application.existing_account_email || '—'}</FieldValue>
          </Field>
        </FieldGrid>
        {application.comments_motivation && (
          <CommentsField>
            <FieldLabel>
              {t('admin.applications.commentsMotivation')}
            </FieldLabel>
            <FieldValue>{application.comments_motivation}</FieldValue>
          </CommentsField>
        )}
      </Section>

      <Section>
        <SectionTitle>{t('admin.applications.timestamps')}</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>{t('admin.applications.createdAt')}</FieldLabel>
            <FieldValue>
              {new Date(application.created_at).toLocaleString()}
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.applications.updatedAt')}</FieldLabel>
            <FieldValue>
              {new Date(application.updated_at).toLocaleString()}
            </FieldValue>
          </Field>
        </FieldGrid>
      </Section>
      {!isFollowUp && (
        <ApproveApplicationDialog
          application={application}
          open={approveDialogOpen}
          onOpenChange={setApproveDialogOpen}
        />
      )}
      <ConfirmDialog
        open={discardDialogOpen}
        title={t('admin.applications.discardTitle')}
        confirmLabel={t('admin.applications.discardConfirm')}
        pending={discardMutation.isPending}
        onCancel={() => {
          setDiscardDialogOpen(false);
          discardMutation.reset();
        }}
        onConfirm={() =>
          discardMutation.mutate({
            path: { application_id: Number(applicationId) },
          })
        }
      >
        {t('admin.applications.discardText')}
        {discardError && (
          <ActionError role="alert">
            {t(
              applicationRefusalKey(discardError) ??
                'admin.applications.discardFailed',
            )}
          </ActionError>
        )}
      </ConfirmDialog>
    </Container>
  );
}

// Styles

const Container = styled.div``;

const BackLink = styled(Link)`
  align-items: center;
  color: var(--blue);
  display: inline-flex;
  font-size: 0.875rem;
  gap: 0.5rem;
  margin-bottom: 1rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const TitleGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const AgreementItem = styled.div`
  align-items: center;
  border-bottom: var(--component-border);
  display: flex;
  justify-content: space-between;
  padding: var(--inner-padding);

  &:last-child {
    border-bottom: none;
  }
`;

const AgreementLabel = styled.span`
  color: var(--grey-800);
  font-size: 0.875rem;
`;

const ScreeningList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: var(--inner-padding);
`;

// Not the agreements' label-plus-Badge row: a screening answer is free text up
// to 2000 characters, so it stacks under its label and keeps its own newlines.
const ScreeningAnswer = styled(FieldValue)`
  white-space: pre-wrap;
`;

const CommentsField = styled(Field)`
  margin-top: 1rem;
  padding: 0 var(--inner-padding) var(--inner-padding);
`;

const LoadingMessage = styled.div`
  color: var(--grey-600);
  padding: 2rem;
  text-align: center;
`;

const ActionError = styled.p`
  color: var(--error);
  font-size: 0.875rem;
  margin: 0 0 1rem;
`;

const ErrorMessage = styled.div`
  color: var(--error);
  padding: 2rem;
  text-align: center;
`;
